"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
/* eslint-disable no-prototype-builtins */
const lodash_1 = __importDefault(require("lodash"));
const axios_1 = __importDefault(require("axios"));
const constants_1 = require("../constants");
const StoreError_1 = __importDefault(require("./StoreError"));
const StoreRetryError_1 = __importDefault(require("./StoreRetryError"));
const chart_helpers_1 = require("../util/chart.helpers");
class RESTStore {
  constructor(dataModifier) {
    this.datasets = {};
    this.modifier = dataModifier || (d => d);
  }
  static datasetID(metric, adjustment, fips) {
    return [String(metric.id), adjustment, fips].filter(a => a && a.length).join('-');
  }
  static fetch(url) {
    return axios_1.default.get(url, {
      headers: {
        'X-Forwarded-For': 'https://usafacts.org'
      }
    }).then(res => res.data).catch(e => {
      console.error(e);
      return new StoreRetryError_1.default(`[RESTStore] could not fetch ${url}`);
    });
  }
  has(id) {
    if (!id) {
      return false;
    }
    const datasetHasId = this.datasets && Object.prototype.hasOwnProperty.call(this.datasets, id);
    if (!datasetHasId || !this.datasets[id].resolved) {
      return false;
    }
    return true;
  }
  static checkMetricValid(dataset) {
    return RESTStore.checkDataValid(dataset) && RESTStore.checkDatasetsValid(dataset);
  }
  static checkDataValid(dataset) {
    if (!dataset.data) {
      return true;
    }
    return !Array.isArray(dataset.data) || dataset.data.length > 0;
  }
  static checkDatasetsValid(dataset) {
    if (!dataset.datasets) {
      return true;
    }
    return !Array.isArray(dataset.datasets) || dataset.datasets.length > 0 && dataset.datasets.every(d => RESTStore.checkDataValid(d));
  }
  static getMetricURLBase(metric, fallbackSource) {
    const combinedSource = metric.source || fallbackSource;
    if (combinedSource && String(combinedSource).toLowerCase() === 'sotu') {
      return constants_1.SOTU_METRIC_BASE;
    }
    return constants_1.API_METRICS_BASE;
  }
  async load(metrics, adjustment, fallbackSource) {
    const [getAllStates, getAllMetrics] = (0, lodash_1.default)(metrics).flatten().partition(m => Object.prototype.hasOwnProperty.call(m, 'states') || m.allStates === true).value();
    const metricsByURLBase = lodash_1.default.groupBy(getAllMetrics, metric => RESTStore.getMetricURLBase(metric, fallbackSource));
    Object.keys(metricsByURLBase).forEach(urlBase => {
      const getMetrics = metricsByURLBase[urlBase].filter(m => !this.has(RESTStore.datasetID(m, adjustment)));
      if (getMetrics.length > 0) {
        const ids = getMetrics.map(m => m.id);
        let url = `${urlBase}?Ids=${ids.join()}`;
        if (adjustment) {
          url += `&Adjustment=${adjustment || ''}`;
        }
        const response = RESTStore.fetch(url);
        getMetrics.forEach(metric => {
          const datasetValue = response.then(datasets => {
            // eslint-disable-next-line eqeqeq
            const dataset = [datasets].flat().find(_ref => {
              let {
                id
              } = _ref;
              return id == metric.id;
            });
            const modified = this.modifier(dataset);
            if (RESTStore.checkMetricValid(modified)) {
              return (0, chart_helpers_1.formatSingleMetric)(modified);
            }
            const error = new StoreError_1.default(`[RESTStore] ${RESTStore.datasetID(metric, adjustment)} has empty data`, dataset);
            return Promise.reject(error);
          });
          datasetValue.resolved = true;
          this.datasets[RESTStore.datasetID(metric, adjustment)] = datasetValue;
        });
      }
    });
    this.loadStates(getAllStates, adjustment, fallbackSource);
  }
  async loadStates(allStateParents, adjustment, fallbackSource) {
    const metricsByURLBase = lodash_1.default.groupBy(allStateParents, metric => RESTStore.getMetricURLBase(metric, fallbackSource));
    Object.keys(metricsByURLBase).forEach(urlBase => {
      const idGroups = lodash_1.default.groupBy(metricsByURLBase[urlBase], 'id');
      lodash_1.default.forEach(idGroups, (metrics, id) => {
        let fipsList;
        if (lodash_1.default.some(metrics, {
          allStates: true
        })) {
          fipsList = Object.keys(constants_1.STATE_CODES);
          if (!lodash_1.default.some(metrics, {
            showUSA: true
          })) {
            fipsList = fipsList.filter(fl => fl !== 'US');
          }
        } else {
          fipsList = (0, lodash_1.default)(metrics).flatMap('states').map('fips').uniq().value();
        }
        fipsList = fipsList.filter(fips => !this.has(RESTStore.datasetID({
          id
        }, adjustment, fips)));
        if (fipsList.length === 0) {
          // Already have all needed states
          return;
        }
        let url = `${urlBase}?Ids=${id}`;
        if (adjustment) {
          url += `&Adjustment=${adjustment || ''}`;
        }
        if (fipsList && fipsList.length) {
          url += `&fips=${fipsList.join(',')}`;
        }
        const response = RESTStore.fetch(url).then(datasets => {
          const modified = this.modifier(datasets[0]);
          if (RESTStore.checkMetricValid(modified)) {
            return modified;
          }
          const error = new StoreError_1.default(`[RESTStore] ${RESTStore.datasetID({
            id
          }, adjustment, '[fips]')} has empty data`, datasets);
          return Promise.reject(error);
        });
        fipsList.forEach(fips => {
          const datasetValue = response.then(dataset => {
            const fipsSet = dataset.datasets.filter(d => d.geo_id === fips);
            if (RESTStore.checkMetricValid(fipsSet[0])) {
              return (0, chart_helpers_1.formatSingleMetric)({
                ...dataset,
                datasets: fipsSet
              });
            }
            const error = new StoreError_1.default(`[RESTStore] ${RESTStore.datasetID({
              id
            }, adjustment, fips)} has empty data`, dataset);
            return Promise.reject(error);
          });
          datasetValue.resolved = true;
          this.datasets[RESTStore.datasetID({
            id
          }, adjustment, fips)] = datasetValue;
        });
      });
    });
  }
  async get(metric, adjustment, fips) {
    return this.datasets[RESTStore.datasetID(metric, adjustment, fips)];
  }
}
exports.default = RESTStore;